import dayjs from 'dayjs'
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from 'react-router-dom'
import { z } from 'zod'

import { ReferralData, referralSchema } from '@/schema.ts'
import { useAppointmentStore } from '@/store.ts'
import { fetchFromApi } from '@/utils/fetchFromApi.ts'

interface ActionData {
  errors?: z.ZodIssue[]
  error?: string
}

interface VerifyResponse {
  birthDate: string
  location: string
  type: string
  zdCode: string
  patient?: {
    email?: string
    phone?: string
    id?: string
  }
  questionnaire?: {
    ids: string[]
    question: string
    answers: { text: string; value: string; id: string }[]
  }
}

interface ErrorResponse {
  error: string
  code?: string
  status: number
}

const checkReferralCode = async ({
  dateOfBirth,
  referralCode,
}: ReferralData): Promise<VerifyResponse | ErrorResponse> => {
  try {
    const response = await fetchFromApi(`verify/`, {
      body: { birthDate: dateOfBirth, zdCode: referralCode },
      method: 'POST',
    })

    if (response.ok) {
      const data: VerifyResponse = await response.json()

      return data
    } else if (response.status === 404) {
      return { error: 'notFound', status: 404 }
    } else if (response.status === 504) {
      const errorData = await response.json()
      if (errorData.error === 'TIMEFF_TIMEOUT') {
        return { error: 'timeffTimeoutXpert', status: 504 }
      }
      return { error: 'unexpectedError', status: 504 }
    } else if (response.status === 500) {
      return {
        error: 'internalServerError',
        status: 500,
      }
    } else if (response.status === 400) {
      const { code } = await response.json()
      return { error: code, status: 400 }
    } else {
      return { error: 'unexpectedError', status: response.status }
    }
  } catch (error) {
    console.error('Error in checkReferralCode:', error)
    return {
      error: 'networkError',
      status: 0,
    }
  }
}

const action = async ({
  request,
}: ActionFunctionArgs): Promise<ActionData | Response> => {
  const { setError, setPatientData, setQuestionnaire, setReferralData } =
    useAppointmentStore.getState()

  const formData = await request.formData()
  const referralData = {
    dateOfBirth: dayjs(
      String(formData.get('dateOfBirth')),
      'DD/MM/YYYY',
      true,
    ).format('YYYY-MM-DD'),
    referralCode: formData.get('referralCode'),
  }

  const result = referralSchema.safeParse(referralData)

  if (!result.success) {
    console.error('Error in referralSchema.safeParse:', result.error.issues)
    return { errors: result.error.issues }
  }

  const response = await checkReferralCode(result.data)

  if ('zdCode' in response) {
    setPatientData({
      dateOfBirth: response.birthDate,
      email: response.patient?.email,
      id: response.patient?.id,
      phone: response.patient?.phone,
    })
    setReferralData({
      location: response.location,
      type: response.type,
      zdCode: response.zdCode,
    })
    if (response.questionnaire) {
      setQuestionnaire(response.questionnaire)
      return redirect('/referral/more-information')
    }
    return redirect('/referral/confirm-details')
  }
  if (response.status === 404) {
    return redirect('/referral/not-found')
  }
  if (response.error === 'timeffTimeoutXpert' || response.status === 504) {
    setError('timeffTimeoutXpert')
    return redirect('/error')
  }
  if (response.error) {
    return { error: response.error }
  }
  return redirect('/referral/not-found')
}

const loader = (_arg: LoaderFunctionArgs) => {
  return null
}

export type { ActionData }
export { action, loader }
